/**
 * QuickSearch - Isolated SearchDrawer open/close logic
 * Handles opening and closing the search drawer for both mobile and desktop
 */

import { clearAllBodyScrollLocks } from 'body-scroll-lock';

export default function () {
  const searchDrawer = document.querySelector('[data-search-drawer]');
  const searchToggle = document.querySelector('[data-search-toggle]');
  const searchCloseButtons = document.querySelectorAll('[data-search-close]');
  const siteOverlay = document.querySelector('[data-site-overlay]');
  const header = document.querySelector('[data-header]');
  const productModal = document.getElementById('productModal'); // Mobile modal
  // Store scroll position
  let scrollPosition = 0;

  if (!searchDrawer) {
    console.warn('SearchDrawer not found');
    return;
  }

  /**
   * Check if we're on mobile
   */
  function isMobile() {
    return window.innerWidth < 990;
  }

  /**
   * Disable body scroll
   */
  function lockScroll() {
    scrollPosition = window.pageYOffset;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollPosition}px`;
    document.body.style.width = '100%';
  }

  /**
   * Enable body scroll
   */
  function unlockScroll() {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollPosition);
  }

  /**
   * Open the SearchDrawer
   * Works for both mobile and desktop
   */
  function openSearchDrawer() {
    // Close hamburger menu if it's open (avoid calling closeAllMenus which would clear our search drawer class)
    const state = document.querySelector('[data-state]');
    if (state && state.classList.contains('State--mobile-menu-open')) {
      state.classList.remove('State--mobile-menu-open');
      clearAllBodyScrollLocks();
    }

    // Add open class to search drawer (desktop)
    searchDrawer.classList.add('quick-search-c-drawer--open');

    // Lock scroll so page behind drawer doesn't scroll
    lockScroll();

    // Show mobile modal if on mobile
    if (isMobile() && productModal) {
      productModal.style.display = 'block';
    }

    // Add open class to search toggle icon
    if (searchToggle) {
      searchToggle.classList.add('IconToggle--open');
    }

    // Show overlay
    if (siteOverlay) {
      siteOverlay.classList.add('SiteOverlay--active');
      if (!isMobile()) {
        siteOverlay.style.cursor = 'default';
      }
    }

    // Add search-open class to Header to remove border
    if (header) {
      header.classList.add('Header--search-open');
    }

    // Focus the search input only on mobile
    // On desktop, user types in the header input, not the drawer input
    if (isMobile()) {
      const input = searchDrawer.querySelector('input');
      if (input) {
        input.focus();
      }
    }

    // Dispatch scroll event
    window.dispatchEvent(new Event('scroll'));

    // Dispatch custom event for other components to listen
    document.dispatchEvent(new CustomEvent('searchDrawerOpened'));
  }

  /**
   * Close the SearchDrawer
   * Works for both mobile and desktop
   */
  function closeSearchDrawer() {
    // Remove open class from search drawer (desktop)
    searchDrawer.classList.remove('quick-search-c-drawer--open');

    // Hide mobile modal if on mobile
    if (productModal) {
      productModal.style.display = 'none';

      // Scroll modal content back to top
      const modalContent = productModal.querySelector('.modal-content, [data-modal-content]');
      if (modalContent) {
        modalContent.scrollTop = 0;
      }

      // Clear mobile search input
      const mobileInputs = document.querySelectorAll('.fs_mobile_form .searchInput');
      mobileInputs.forEach((input) => {
        input.value = '';
      });

      // Remove GO button if it exists
      const goButton = document.querySelector('.fs_go_button');
      if (goButton) {
        goButton.remove();
      }
    }

    // Unlock scroll
    unlockScroll();

    // Remove open class from search toggle icon
    if (searchToggle) {
      searchToggle.classList.remove('IconToggle--open');
    }

    // Remove overlay
    if (siteOverlay) {
      siteOverlay.classList.remove('SiteOverlay--active');
    }

    // Remove search-open class from Header
    if (header) {
      header.classList.remove('Header--search-open');
    }

    // Blur all search inputs
    const inputs = searchDrawer.querySelectorAll('input');
    inputs.forEach((input) => {
      input.blur();
    });

    // Dispatch scroll event
    window.dispatchEvent(new Event('scroll'));

    // Dispatch custom event for other components to listen
    document.dispatchEvent(new CustomEvent('searchDrawerClosed'));
  }

  /**
   * Toggle the SearchDrawer (open if closed, close if open)
   */
  function toggleSearchDrawer() {
    if (isSearchDrawerOpen()) {
      closeSearchDrawer();
    } else {
      openSearchDrawer();
    }
  }

  /**
   * Check if SearchDrawer is currently open
   */
  function isSearchDrawerOpen() {
    const desktopOpen = searchDrawer.classList.contains('quick-search-c-drawer--open');
    const mobileOpen = productModal && productModal.style.display === 'block';
    return desktopOpen || mobileOpen;
  }

  // Event Listeners

  // 1. Search toggle (clicking search icon - both desktop and mobile)
  document.addEventListener('click', (event) => {
    const searchToggleElement = event.target.closest('[data-search-toggle]');
    const mobileSearchIcon = event.target.closest('.MobileBar__icon--search');

    if (searchToggleElement || mobileSearchIcon) {
      // Don't toggle if user clicked inside the desktop search form (input, GO button, etc.)
      if (event.target.closest('[data-quick-search-desktop-drawer-input]')) return;

      event.preventDefault();
      event.stopPropagation();
      toggleSearchDrawer();
    }
  });

  // 2. Desktop search form click
  document.addEventListener('click', (event) => {
    const desktopSearchForm = event.target.closest('[data-quick-search-desktop-drawer-input]');
    if (desktopSearchForm) {
      if (!isSearchDrawerOpen()) {
        event.preventDefault();
        openSearchDrawer();
      }
    }
  });

  // 2b. Desktop search form keyboard focus (Tab into input)
  document.addEventListener('focusin', (event) => {
    const desktopSearchForm = event.target.closest('[data-quick-search-desktop-drawer-input]');
    if (desktopSearchForm && !isSearchDrawerOpen()) {
      openSearchDrawer();
    }
  });

  // 3. Close buttons (chevron-left icon and X icon)
  searchCloseButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      closeSearchDrawer();
    });
  });

  // 4. Site overlay click (click outside)
  if (siteOverlay) {
    siteOverlay.addEventListener('click', () => {
      if (isSearchDrawerOpen()) {
        closeSearchDrawer();
      }
    });
  }

  // 5. Escape key
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isSearchDrawerOpen()) {
      closeSearchDrawer();
    }
  });

  // 6. Handle viewport resize - keep scroll locked while drawer is open
  window.addEventListener('resize', () => {
    if (isSearchDrawerOpen()) {
      lockScroll();
    }
  });

  // Export functions for external use
  window.openSearchDrawer = openSearchDrawer;
  window.closeSearchDrawer = closeSearchDrawer;
  window.toggleSearchDrawer = toggleSearchDrawer;
  window.isSearchDrawerOpen = isSearchDrawerOpen;
}
